import { useEffect, useState } from "react";
import { playChatPop } from "../../lib/sounds";

const QUIET_PERIOD_MS = 1_500;
const HINT_DURATION_MS = 5_000;
const OVERLAYS = "dialog[open], [data-popover]";

type Options = {
  userId: string | undefined;
  hasClosedChats: boolean;
  hasClosedConversation: boolean;
};

export function useChatFinderHint({
  userId,
  hasClosedChats,
  hasClosedConversation,
}: Options) {
  const [finderOpen, setFinderOpen] = useState(false);
  const [visibleForUserId, setVisibleForUserId] = useState<string | null>(null);
  const storageKey = userId ? `chat-finder-discovered:${userId}` : null;
  const hintVisible =
    userId !== undefined &&
    visibleForUserId === userId &&
    hasClosedConversation &&
    !finderOpen;

  const markFinderDiscovered = () => {
    if (storageKey) localStorage.setItem(storageKey, "1");
    setVisibleForUserId(null);
  };

  useEffect(() => {
    if (!finderOpen || !hasClosedChats || !storageKey) return;
    localStorage.setItem(storageKey, "1");
    // oxlint-disable-next-line react/react-compiler -- visibility follows discovery in the external dropdown
    setVisibleForUserId(null);
  }, [finderOpen, hasClosedChats, storageKey]);

  useEffect(() => {
    if (
      !userId ||
      !storageKey ||
      !hasClosedConversation ||
      finderOpen ||
      hintVisible ||
      localStorage.getItem(storageKey)
    )
      return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    let composing = false;
    let overlayOpen = Boolean(document.querySelector(OVERLAYS));
    const schedule = () => {
      clearTimeout(timer);
      if (document.visibilityState !== "visible" || composing || overlayOpen)
        return;
      timer = setTimeout(() => {
        if (
          document.visibilityState !== "visible" ||
          document.querySelector(OVERLAYS) ||
          localStorage.getItem(storageKey)
        )
          return;
        localStorage.setItem(storageKey, "1");
        setVisibleForUserId(userId);
        playChatPop();
      }, QUIET_PERIOD_MS);
    };
    const onCompositionStart = () => {
      composing = true;
      clearTimeout(timer);
    };
    const onCompositionEnd = () => {
      composing = false;
      schedule();
    };
    const observer = new MutationObserver(() => {
      const next = Boolean(document.querySelector(OVERLAYS));
      if (next === overlayOpen) return;
      overlayOpen = next;
      schedule();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["open"],
    });
    const events = ["keydown", "input", "pointerdown", "visibilitychange"];
    for (const event of events)
      document.addEventListener(event, schedule, true);
    document.addEventListener("compositionstart", onCompositionStart, true);
    document.addEventListener("compositionend", onCompositionEnd, true);
    schedule();
    return () => {
      clearTimeout(timer);
      observer.disconnect();
      for (const event of events)
        document.removeEventListener(event, schedule, true);
      document.removeEventListener(
        "compositionstart",
        onCompositionStart,
        true
      );
      document.removeEventListener("compositionend", onCompositionEnd, true);
    };
  }, [userId, storageKey, hasClosedConversation, finderOpen, hintVisible]);

  useEffect(() => {
    if (!hintVisible) return;
    const hide = () => setVisibleForUserId(null);
    const timer = setTimeout(hide, HINT_DURATION_MS);
    const events = ["keydown", "input", "pointerdown", "visibilitychange"];
    for (const event of events) document.addEventListener(event, hide, true);
    return () => {
      clearTimeout(timer);
      for (const event of events)
        document.removeEventListener(event, hide, true);
    };
  }, [hintVisible]);

  return {
    hintVisible,
    onFinderOpenChange: setFinderOpen,
    markFinderDiscovered,
  };
}
